import React, { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Paper, Stack, useTheme } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ResourceChange } from "./ChangeLog";
import ResourceChangeLogRowDetails from "./ChangeLogRowDetails";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);

interface ResourceChangeLogRowProps {
  changes: Array<ResourceChange>;
  date: string;
}

export default function ResourceChangeLogRow(props: ResourceChangeLogRowProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const now = dayjs();
  const changeDate = dayjs(props.date);
  const days = now.diff(changeDate, "days");
  const dateLabel = changeDate.utc().startOf("day").format("ddd, DD MMM YYYY");
  const duration = days > 0
    ? i18n.t("change-log-row.duration", {days: days})
    : i18n.t("today");
  const changesLabel = i18n.t("change-log-row.history", {count: props.changes.length, duration: duration});

  return (
    <Paper
      key={props.date}
      onClick={() => setIsExpanded(!isExpanded)}
      sx={{p: 0}}
      variant="outlined"
    >
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            borderBottom: 1,
            borderColor: theme.colors.secondary.lighter,
            borderRadius: 0, 
            p: 1,
            "&.Mui-expanded": {
              minHeight: 0
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
              "&.Mui-expanded": {
                margin: 0
              }
            }
          }}
        >
          <Stack direction="row" sx={{alignItems: "center", flex: 1, justifyContent: "space-between", mr: 1}}>
            <span>{dateLabel}</span>
            <span>{changesLabel}</span>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{p: 1}}>
          <Stack sx={{gap: 1}}>
            {props.changes.toReversed().map((change, index) => {
              return (
                <ResourceChangeLogRowDetails key={`${change.timestamp}-${index}`} change={change} />
              )
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}